import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import {
  fetchPromotions, createPromotion, updatePromotion, deletePromotion,
  reorderPromotions, uploadImage, resolveUrl, getErrorMessage,
} from "@/lib/api";

const emptyForm = { image_url: "", title_en: "", title_ar: "", subtitle_en: "", subtitle_ar: "", is_active: true };

export default function PromotionsManager() {
  const { t, lang } = useLang();
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await fetchPromotions(false);
      setPromos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: res.relative_url || res.path }));
      toast.success(t.itemUpdated);
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.image_url) { toast.error(t.promoImageRequired); return; }
    setBusy(true);
    try {
      await createPromotion(form);
      toast.success(t.itemAdded);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await updatePromotion(p.id, { is_active: !p.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await deletePromotion(p.id);
      toast.success(t.itemDeleted);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const move = async (idx, dir) => {
    const next = [...promos];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setPromos(next);
    try {
      await reorderPromotions(next.map((p) => p.id));
    } catch (err) {
      toast.error(getErrorMessage(err));
      load();
    }
  };

  return (
    <div className="space-y-8" data-testid="promotions-manager">
      {/* Add form */}
      <form onSubmit={handleAdd} className="rounded-2xl bg-white border border-black/5 p-6 space-y-5">
        <p className="text-xs uppercase tracking-[0.2em] text-[#0F5F63] font-semibold">{t.addPromotion}</p>

        <div>
          <Label className="text-xs text-[#5C6B6D]">{t.promoImage}</Label>
          <div className="mt-2 flex items-center gap-4">
            <Input
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="https://… / /api/files/…"
              data-testid="promo-image-url"
            />
            <Button type="button" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}
              className="gap-2 shrink-0 border-[#0F5F63]/30 text-[#0F5F63]" data-testid="promo-upload-btn">
              <Upload className="w-4 h-4" /> {t.uploadImage}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleUpload} data-testid="promo-image-file" />
          </div>
          {form.image_url && (
            <img src={resolveUrl(form.image_url)} alt="preview" className="mt-3 w-full max-w-md aspect-[21/8] rounded-xl object-cover" />
          )}
          <p className="text-[10px] text-[#5C6B6D] mt-2">{t.promoImageHint}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#5C6B6D]">{t.promoTitleEn}</Label>
            <Input className="mt-2" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="promo-title-en" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6B6D]">{t.promoTitleAr}</Label>
            <Input className="mt-2" dir="rtl" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} data-testid="promo-title-ar" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6B6D]">{t.promoSubtitleEn}</Label>
            <Input className="mt-2" value={form.subtitle_en} onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })} data-testid="promo-subtitle-en" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6B6D]">{t.promoSubtitleAr}</Label>
            <Input className="mt-2" dir="rtl" value={form.subtitle_ar} onChange={(e) => setForm({ ...form, subtitle_ar: e.target.value })} data-testid="promo-subtitle-ar" />
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl bg-[#F4F2EB] px-4 py-3">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="promo-form-active" />
          <Label className="cursor-pointer">{form.is_active ? t.active : t.inactive}</Label>
        </div>

        <Button type="submit" disabled={busy} className="gap-2 bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7]" data-testid="promo-add-btn">
          <Plus className="w-4 h-4" /> {t.addPromotion}
        </Button>
      </form>

      {/* List */}
      <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
        <div className="px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">
          {t.tabPromotions} ({promos.length})
        </div>
        {promos.length === 0 ? (
          <div className="p-8 text-sm text-[#5C6B6D] text-center">{t.noPromotions}</div>
        ) : (
          promos.map((p, idx) => (
            <div key={p.id} className="flex items-center gap-4 px-6 py-4 border-t border-black/5" data-testid={`promo-row-${p.id}`}>
              <img src={resolveUrl(p.image_url)} alt="" className="w-28 h-14 rounded-lg object-cover shrink-0 bg-[#F4F2EB]" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1A1A1A] truncate">{lang === "ar" ? (p.title_ar || p.title_en) : (p.title_en || p.title_ar) || "—"}</p>
                <p className="text-xs text-[#5C6B6D] truncate">{lang === "ar" ? (p.subtitle_ar || p.subtitle_en) : (p.subtitle_en || p.subtitle_ar)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" disabled={idx === 0} onClick={() => move(idx, -1)}
                  data-testid={`promo-up-${p.id}`} className="rounded-full text-[#5C6B6D] hover:bg-[#F4F2EB]"><ArrowUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" disabled={idx === promos.length - 1} onClick={() => move(idx, 1)}
                  data-testid={`promo-down-${p.id}`} className="rounded-full text-[#5C6B6D] hover:bg-[#F4F2EB]"><ArrowDown className="w-4 h-4" /></Button>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={p.is_active} onCheckedChange={() => toggleActive(p)} data-testid={`promo-active-${p.id}`} />
                <span className="text-xs text-[#5C6B6D] w-12">{p.is_active ? t.active : t.inactive}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`}
                className="rounded-full text-[#E27B58] hover:bg-[#E27B58]/10"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
